import { Component, Inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { SeoService } from 'src/app/services/seo.service';
import { Meta } from '@angular/platform-browser';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxMaskDirective } from 'ngx-mask';
import { Observable, forkJoin } from 'rxjs';
import { PortfolioResult } from 'src/app/pages/_components/index-calculator-v2026/api-response.model';
import { TARGET_DATE_PORTFOLIOS } from 'src/app/pages/_components/index-calculator-v2026/investment-choices.data';
import { SafePipe } from 'src/app/pipes/safe.pipe';
import { GlidePathChartComponent } from './glide-path-chart/glide-path-chart.component';
import { ImportantDisclosuresComponent } from 'src/app/pages/_components/important-disclosures/important-disclosures.component';

type ReturnCell = { totalReturn: number; annualizedReturn: number; months: number } | null;
type ReturnRow = { name: string; symbol: string; cells: ReturnCell[] };
type FundDescription = { name: string; symbol: string; description: string; equityMin: number | null; equityMax: number | null };

@Component({
    selector: 'app-target-date-performance',
    templateUrl: './target-date-performance.component.html',
    styleUrls: ['./target-date-performance.component.scss'],
    imports: [CommonModule, RouterModule, FormsModule, NgxMaskDirective, SafePipe, GlidePathChartComponent, ImportantDisclosuresComponent]
})
export class TargetDatePerformanceComponent implements OnInit {

  title = "IFC Target Retirement Index Portfolios Performance";

  // Signals: the injected ChangeDetectorRef can be a destroyed ViewRef after SSR
  // hydration, silently no-oping detectChanges(); signal writes mark the live
  // view dirty directly.
  returnPeriods = signal<Array<{ label: string; startDate: Date; endDate: Date }>>([]);
  returnRows = signal<ReturnRow[]>([]);
  returnsLoading = signal(false);
  returnsError = signal<string | null>(null);
  fundDescriptions = signal<FundDescription[]>([]);
  startingAssets = 100000;
  private latestEndDate: Date | null = null;
  /** Risk-based portfolio descriptions fetched from the investment API. */
  private rbDescriptions = new Map<string, string>();

  /** Mirrors the Index Calculator preset for these investments/periods. */
  get calculatorQueryParams() {
    return {
      assets: this.startingAssets,
      investments: 'td2080~td2075~td2070~td2065~td2060~td2055~td2050~td2045~td2040~td2035~td2030~td2025~td2020~td1933~7846~7866~7886',
      periods: '1yr,3yr,5yr,10yr,ifc-inception',
      tab: '1yr',
      detail: 'value',
      cats: 'td',
    };
  }

  // 1933 = Retirement Income vintage
  private readonly TD_VINTAGES = [2080, 2075, 2070, 2065, 2060, 2055, 2050, 2045, 2040, 2035, 2030, 2025, 2020, 1933];
  private readonly RISK_BASED_PORTFOLIOS = [
    { name: 'Catholic Values Target Risk Model Portfolio 80 (Aggressive)', symbol: 'port80srwf_ifa' },
    { name: 'Catholic Values Target Risk Model Portfolio 60 (Moderate)', symbol: 'port60srwf_ifa' },
    { name: 'Catholic Values Target Risk Model Portfolio 40 (Conservative)', symbol: 'port40srwf_ifa' },
  ];

  constructor(private seoservice: SeoService, private meta: Meta, private http: HttpClient, @Inject(PLATFORM_ID) private platformId: Object) { }

  ngOnInit() {
    this.seoservice.updateTitle(this.title);
    this.meta.addTag({ name: 'keywords', content: 'target date retirement portfolios performance, catholic values index portfolios returns' });
    this.meta.addTag({ name: 'description', content: 'Performance of Investing for Catholics Target Retirement Index Portfolios.' });
    this.meta.addTag({ name: 'author', content: 'Investing for Catholics (IFC)' });
    this.meta.addTag({ name: 'robots', content: 'index, follow' });

    if (isPlatformBrowser(this.platformId)) {
      this.loadRbDescriptions();
      this.loadReturnsTable();
    }
  }

  private loadRbDescriptions(): void {
    const url = `https://data2.ifa.com/odata/v1/investment/?$select=symbol,description&$filter=id in (7846,7866,7886)`;
    this.http.get<Array<{ symbol: string; description: string }>>(url).subscribe({
      next: resp => {
        for (const item of resp ?? []) {
          this.rbDescriptions.set(item.symbol, item.description ?? '');
        }
        // Re-apply in case the returns table finished first
        if (this.fundDescriptions().length) {
          this.fundDescriptions.update(rows => rows.map(r =>
            this.rbDescriptions.has(r.symbol) ? { ...r, description: this.rbDescriptions.get(r.symbol)! } : r));
        }
      },
      error: () => { /* descriptions are optional */ }
    });
  }

  /** Asks the API for the latest available data month first, so the table
   *  advances automatically each month as new performance data is published. */
  private loadReturnsTable(): void {
    const DATE_RANGE_URL = 'https://data2.ifa.com/api/v1/investment/performance/monthly/grr/?startDates=start&endDates=end&ids=646';
    this.returnsLoading.set(true);
    this.http.get<{ investments: Array<{ periods: Array<{ subtitle: { start: string; end: string } }> }> }>(DATE_RANGE_URL).subscribe({
      next: dateRange => {
        const end = dateRange?.investments?.[0]?.periods?.[0]?.subtitle?.end; // 'M/D/YYYY'
        if (!end) {
          this.returnsLoading.set(false);
          this.returnsError.set('Performance data is temporarily unavailable.');
          return;
        }
        const [m, , y] = end.split('/').map(Number);
        this.latestEndDate = new Date(y, m, 0); // last day of latest data month
        this.fetchReturns(this.latestEndDate);
      },
      error: () => {
        this.returnsLoading.set(false);
        this.returnsError.set('Performance data is temporarily unavailable.');
      }
    });
  }

  /** Re-runs the API calls with the user's starting amount (affects the fee schedule applied). */
  recalculateReturns(): void {
    if (this.returnsLoading()) return;
    // ngx-mask emits the unmasked value as a string
    this.startingAssets = Number(this.startingAssets);
    if (!this.startingAssets || this.startingAssets < 1) {
      this.startingAssets = 100000;
    }
    if (this.latestEndDate) {
      this.fetchReturns(this.latestEndDate);
    } else {
      this.loadReturnsTable();
    }
  }

  private fetchReturns(endDate: Date): void {
    this.returnsLoading.set(true);
    this.returnsError.set(null);
    const y = endDate.getFullYear();
    const mIdx = endDate.getMonth();
    this.returnPeriods.set([
      { label: '1 Year',              startDate: new Date(y, mIdx + 1 - 12,  1), endDate },
      { label: '3 Years',             startDate: new Date(y, mIdx + 1 - 36,  1), endDate },
      { label: '5 Years',             startDate: new Date(y, mIdx + 1 - 60,  1), endDate },
      { label: '10 Years',            startDate: new Date(y, mIdx + 1 - 120, 1), endDate },
      { label: 'Since IFC Inception', startDate: new Date(2009, 0, 1),           endDate },
    ]);

    const baseUrl    = 'https://data2.ifa.com/odata/v1/returns/monthly/domain';
    const dollar     = String(this.startingAssets);
    const startMs    = this.returnPeriods().map(p => p.startDate.getTime());
    const startDates = this.returnPeriods().map(p => this.fmt(p.startDate));
    const endStr     = this.fmt(endDate);
    const endDates   = this.returnPeriods().map(() => endStr).join('|');

    // One call per target date vintage: rendered from the port92srwf_ifa glide
    // path with domain start 7/1/(vintage − 84), same as the Index Calculator.
    const tdCalls: Observable<PortfolioResult[]>[] = this.TD_VINTAGES.map(vintage => {
      const domainStart   = `7/1/${vintage - 84}`;
      const domainStartMs = new Date(vintage - 84, 6, 1).getTime();
      const clamped = startDates.map((sd, j) => startMs[j] < domainStartMs ? domainStart : sd);
      const params = new HttpParams()
        .set('domainStart',     domainStart)
        .set('domainEnd',       endStr)
        .set('startDate',       clamped.join('|'))
        .set('endDate',         endDates)
        .set('portfolios',      'port92srwf_ifa')
        .set('glidePath',       'true')
        .set('dollar',          dollar)
        .set('customGlidePath', 'SRWFTargetDate')
        .set('showReturns',     'true');
      return this.http.get<PortfolioResult[]>(`${baseUrl}?${params.toString()}`);
    });

    // Risk based portfolios: one direct call, no glide path
    const rbParams = new HttpParams()
      .set('domainStart',     this.fmt(new Date(Math.min(...startMs))))
      .set('domainEnd',       endStr)
      .set('startDate',       startDates.join('|'))
      .set('endDate',         endDates)
      .set('portfolios',      this.RISK_BASED_PORTFOLIOS.map(p => p.symbol).join('|'))
      .set('glidePath',       'false')
      .set('dollar',          dollar)
      .set('customGlidePath', '')
      .set('showReturns',     'true');
    const rbCall$ = this.http.get<PortfolioResult[]>(`${baseUrl}?${rbParams.toString()}`);

    forkJoin([...tdCalls, rbCall$]).subscribe({
      next: results => {
        const rows: ReturnRow[] = [];
        const descs: FundDescription[] = [];
        const equityRange = (res?: PortfolioResult): { min: number | null; max: number | null } => {
          const eqs = (res?.periods ?? [])
            .flatMap(p => p.monthly ?? [])
            .map(m => /^port(\d+)srwf_ifa$/.exec(m.symbol))
            .filter((m): m is RegExpExecArray => m !== null)
            .map(m => parseInt(m[1], 10));
          return eqs.length ? { min: Math.min(...eqs), max: Math.max(...eqs) } : { min: null, max: null };
        };
        this.TD_VINTAGES.forEach((vintage, i) => {
          const name = vintage === 1933
            ? 'Catholic Values Retirement Income Model Portfolio'
            : `Catholic Values Target Retirement Model Portfolio ${vintage}`;
          const symbol = `td${vintage}`;
          rows.push({ name, symbol, cells: this.toCells(results[i]?.[0]) });
          const choice = TARGET_DATE_PORTFOLIOS.find(c => c.symbol === symbol);
          const range = equityRange(results[i]?.[0]);
          descs.push({ name, symbol, description: choice?.description ?? '', equityMin: range.min, equityMax: range.max });
        });
        const rbResults = results[results.length - 1] ?? [];
        this.RISK_BASED_PORTFOLIOS.forEach(def => {
          const res = rbResults.find(r => r.portfolio === def.symbol);
          rows.push({ name: def.name, symbol: def.symbol, cells: this.toCells(res) });
          const range = equityRange(res);
          descs.push({
            name: def.name,
            symbol: def.symbol,
            description: this.rbDescriptions.get(def.symbol) ?? '',
            equityMin: range.min,
            equityMax: range.max,
          });
        });
        this.returnRows.set(rows);
        this.fundDescriptions.set(descs);
        this.returnsLoading.set(false);
      },
      error: () => {
        this.returnsLoading.set(false);
        this.returnsError.set('Performance data is temporarily unavailable.');
      }
    });
  }

  private toCells(res?: PortfolioResult): ReturnCell[] {
    return this.returnPeriods().map((_, i) => {
      const p = res?.periods?.[i];
      return p ? { totalReturn: p.totalReturn, annualizedReturn: p.annualizedReturn, months: p.months } : null;
    });
  }

  private fmt(d: Date): string {
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
  }

}
